import type { FeedScopeEntry } from "@keydials/api-client";
import { DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "@keydials/dial-workspace";

/**
 * The set logic behind the Feeds paint canvas (KEY-257 phase 2): derive coverage from a feed's scope
 * rows, and toggle a section or action point in and out of them.
 *
 * ⚠️ **Include-only, and empty means empty.** The sibling scope helpers in web-admin carry the opposite
 * convention — an empty key list means *everything* — and reusing it here would mean a sharer who
 * unticked every wedge was feeding their whole dial. There is no "universal" state in this module and
 * no path that converts one: no rows is no disclosure, which is what the server enforces too.
 *
 * Kept apart from the component because this is the part that decides what actually gets disclosed;
 * a component test would exercise it only incidentally.
 */

export type FeedPaintState = "full" | "partial" | "none";

/** The shape the canvas needs from a dial — the instance's resolved structure, narrowed. */
export type FeedPaintItem = { key: string; name: string };
export type FeedPaintSegment = { key: string; label: string; items?: FeedPaintItem[] };
export type FeedPaintSection = { key: string; label: string; segments: FeedPaintSegment[] };

export type FeedPaintCoverage = {
  sections: {
    key: string;
    state: FeedPaintState;
    segments: { key: string; state: FeedPaintState; items: { key: string; fed: boolean }[] }[];
  }[];
  /** Sections fed in whole or in part. */
  fedSections: number;
  /** Action points fed, counting those covered by a whole-section row. */
  fedSegments: number;
  /** Items fed, across every fed action point. */
  fedItems: number;
  totalSections: number;
  totalSegments: number;
  /** Items on the FED action points only — the denominator a sharer is actually choosing within. */
  totalItems: number;
  /** True when at least one row promises whatever is added to its section later. */
  openEnded: boolean;
};

/** A scope row that names a whole section, if one exists. */
function wholeSectionRow(scopes: FeedScopeEntry[], sectionKey: string): FeedScopeEntry | undefined {
  return scopes.find((s) => s.sectionKey === sectionKey && !s.segmentKey);
}

function coversSegment(scopes: FeedScopeEntry[], sectionKey: string, segmentKey: string): boolean {
  return scopes.some(
    (s) => s.sectionKey === sectionKey && (!s.segmentKey || s.segmentKey === segmentKey),
  );
}

/** Whether the action point is fed WHOLE — by its section or by its own row — rather than item by item. */
function coversWholeSegment(scopes: FeedScopeEntry[], sectionKey: string, segmentKey: string): boolean {
  return scopes.some(
    (s) => s.sectionKey === sectionKey && (!s.segmentKey || (s.segmentKey === segmentKey && !s.itemKey)),
  );
}

function coversItem(scopes: FeedScopeEntry[], sectionKey: string, segmentKey: string, itemKey: string): boolean {
  if (coversWholeSegment(scopes, sectionKey, segmentKey)) {
    return true;
  }
  return scopes.some((s) => s.sectionKey === sectionKey && s.segmentKey === segmentKey && s.itemKey === itemKey);
}

/** What the canvas paints: per-segment coverage, folded up into per-section coverage. */
export function feedCoverage(sections: FeedPaintSection[], scopes: FeedScopeEntry[]): FeedPaintCoverage {
  const painted = sections.map((section) => {
    const segments = section.segments.map((segment) => ({
      key: segment.key,
      state: (coversSegment(scopes, section.key, segment.key) ? "full" : "none") as FeedPaintState,
      items: (segment.items ?? []).map((item) => ({
        key: item.key,
        fed: coversItem(scopes, section.key, segment.key, item.key),
      })),
    }));
    const fed = segments.filter((s) => s.state === "full").length;
    // A section with no action points is "full" only if a whole-section row names it — there is nothing
    // else that could cover it, and calling it full because zero of zero segments matched would paint
    // an empty section as fed.
    const state: FeedPaintState =
      segments.length === 0
        ? wholeSectionRow(scopes, section.key)
          ? "full"
          : "none"
        : fed === segments.length
          ? "full"
          : fed === 0
            ? "none"
            : "partial";
    return { key: section.key, state, segments };
  });

  return {
    sections: painted,
    fedSections: painted.filter((s) => s.state !== "none").length,
    fedSegments: painted.reduce((n, s) => n + s.segments.filter((g) => g.state === "full").length, 0),
    fedItems: painted.reduce(
      (n, s) => n + s.segments.filter((g) => g.state === "full").reduce((m, g) => m + g.items.filter((i) => i.fed).length, 0),
      0,
    ),
    totalSections: sections.length,
    totalSegments: sections.reduce((n, s) => n + s.segments.length, 0),
    // Only the FED action points count towards the denominator: "31 of 44 items" is about what is on the
    // table, not every item on the dial, most of which was never offered.
    totalItems: painted.reduce(
      (n, s) => n + s.segments.filter((g) => g.state === "full").reduce((m, g) => m + g.items.length, 0),
      0,
    ),
    openEnded: scopes.some((s) => s.includeFutureChildren),
  };
}

function row(sectionKey: string, segmentKey: string | null, includeFutureChildren = false): FeedScopeEntry {
  return { sectionKey, segmentKey, itemKey: null, attributeKey: null, includeFutureChildren };
}

/**
 * Re-attaches the attribute rows that are still meaningful after a node toggle.
 *
 * The node toggles rebuild a whole section's rows, which would otherwise silently drop any attributes
 * named on it — including on siblings the change never touched. An attribute whose node is no longer fed
 * SHOULD go (the field cannot travel without the node it sits on); one whose node survives should not.
 */
function keepAttributes(previous: FeedScopeEntry[], next: FeedScopeEntry[]): FeedScopeEntry[] {
  const survivors = previous.filter((a) => {
    if (!a.attributeKey) return false;
    if (a.itemKey) return coversItem(next, a.sectionKey, a.segmentKey!, a.itemKey);
    if (a.segmentKey) return coversSegment(next, a.sectionKey, a.segmentKey);
    return next.some((s) => s.sectionKey === a.sectionKey);
  });
  return survivors.length === 0 ? next : [...next, ...survivors];
}

/** An item row. Never open-ended: an item has no children of its own to acquire later. */
function rowForItem(sectionKey: string, segmentKey: string, itemKey: string): FeedScopeEntry {
  return { sectionKey, segmentKey, itemKey, attributeKey: null, includeFutureChildren: false };
}

/**
 * Toggles a whole section. Fully or partly fed becomes not fed at all; otherwise the section's rows are
 * replaced by one whole-section row, which is both smaller and what carries the open-ended promise.
 */
export function toggleSection(
  sections: FeedPaintSection[],
  all: FeedScopeEntry[],
  sectionKey: string,
  openEnded = false,
): FeedScopeEntry[] {
  const scopes = all.filter((s) => !s.attributeKey);
  const others = scopes.filter((s) => s.sectionKey !== sectionKey);
  const coverage = feedCoverage(sections, scopes).sections.find((s) => s.key === sectionKey);
  const next = !coverage || coverage.state !== "none" ? others : [...others, row(sectionKey, null, openEnded)];
  return keepAttributes(all, next);
}

/**
 * Toggles one action point. Turning one off inside a whole-section row is the case worth care: the row
 * is expanded into the siblings that survive, so removing one action point cannot silently keep feeding
 * it — and if none survive, the section leaves the scope entirely rather than lingering as an empty
 * promise.
 */
export function toggleSegment(
  sections: FeedPaintSection[],
  all: FeedScopeEntry[],
  sectionKey: string,
  segmentKey: string,
): FeedScopeEntry[] {
  const scopes = all.filter((s) => !s.attributeKey);
  const section = sections.find((s) => s.key === sectionKey);
  if (!section) return all;

  const others = scopes.filter((s) => s.sectionKey !== sectionKey);
  const covered = coversSegment(scopes, sectionKey, segmentKey);

  if (!covered) {
    const mine = scopes.filter((s) => s.sectionKey === sectionKey);
    const added = [...mine, row(sectionKey, segmentKey)];
    // Every action point ticked individually is the same disclosure as the whole section: collapse it,
    // so the row set stays the smallest thing that says what is meant.
    const every = section.segments.every((seg) => added.some((s) => s.segmentKey === seg.key));
    return keepAttributes(
      all,
      every && section.segments.length > 0 ? [...others, row(sectionKey, null)] : [...others, ...added],
    );
  }

  const whole = wholeSectionRow(scopes, sectionKey);
  if (whole) {
    const expanded = section.segments
      .filter((seg) => seg.key !== segmentKey)
      .map((seg) => row(sectionKey, seg.key));
    return keepAttributes(all, [...others, ...expanded]);
  }

  return keepAttributes(all, [...others, ...scopes.filter((s) => s.sectionKey === sectionKey && s.segmentKey !== segmentKey)]);
}

/**
 * Toggles ONE item inside an action point that is already being fed. The interesting case is unticking an
 * item on an action point fed whole: the covering row is expanded into a row per surviving item, exactly
 * as {@link toggleSegment} expands a whole-section row. Leave it standing and the item the sharer just
 * unticked would keep being fed — the row still covers it.
 *
 * Re-ticking the last missing item collapses back to a single action-point row, and unticking the last
 * one removes the action point altogether rather than leaving a row that feeds nothing.
 */
export function toggleItem(
  sections: FeedPaintSection[],
  all: FeedScopeEntry[],
  sectionKey: string,
  segmentKey: string,
  itemKey: string,
): FeedScopeEntry[] {
  const scopes = all.filter((s) => !s.attributeKey);
  const section = sections.find((s) => s.key === sectionKey);
  const segment = section?.segments.find((g) => g.key === segmentKey);
  const items = segment?.items ?? [];
  if (!section || !segment || items.length === 0) {
    return all;
  }

  // Everything about this action point is rewritten below, so drop its rows — including any whole-section
  // row, whose other action points are re-added explicitly.
  const wholeSection = scopes.find((s) => s.sectionKey === sectionKey && !s.segmentKey);
  const others = scopes.filter((s) => s.sectionKey !== sectionKey);
  const siblings = wholeSection
    ? section.segments.filter((g) => g.key !== segmentKey).map((g) => row(sectionKey, g.key))
    : scopes.filter((s) => s.sectionKey === sectionKey && s.segmentKey !== segmentKey);

  const fedNow = items.filter((i) => coversItem(scopes, sectionKey, segmentKey, i.key)).map((i) => i.key);
  const next = fedNow.includes(itemKey) ? fedNow.filter((k) => k !== itemKey) : [...fedNow, itemKey];

  if (next.length === 0) {
    return keepAttributes(all, [...others, ...siblings]);
  }
  if (next.length === items.length) {
    return keepAttributes(all, [...others, ...siblings, row(sectionKey, segmentKey)]);
  }
  return keepAttributes(all, [...others, ...siblings, ...next.map((k) => rowForItem(sectionKey, segmentKey, k))]);
}

/**
 * Turns one attribute on or off for a node that is already being fed. Attributes start off — they carry
 * free text, files and named people, and sharing those across an organisational boundary by default is
 * the wrong default — so this is always an explicit act.
 *
 * The row carries the node's own path, so it never widens what is fed: the node is already covered, and
 * the pruning rules read the path the same way whether or not an attribute rides on it.
 */
export function toggleAttribute(
  scopes: FeedScopeEntry[],
  sectionKey: string,
  segmentKey: string | null,
  itemKey: string | null,
  attributeKey: string,
): FeedScopeEntry[] {
  const matches = (s: FeedScopeEntry) =>
    s.attributeKey === attributeKey
    && s.sectionKey === sectionKey
    && (s.segmentKey ?? null) === segmentKey
    && (s.itemKey ?? null) === itemKey;

  if (scopes.some(matches)) {
    return scopes.filter((s) => !matches(s));
  }
  return [...scopes, { sectionKey, segmentKey, itemKey, attributeKey, includeFutureChildren: false }];
}

/** Whether a feed names this attribute on this node. */
export function attributeFed(
  scopes: FeedScopeEntry[],
  sectionKey: string,
  segmentKey: string | null,
  itemKey: string | null,
  attributeKey: string,
): boolean {
  return scopes.some(
    (s) =>
      s.attributeKey === attributeKey
      && s.sectionKey === sectionKey
      && (s.segmentKey ?? null) === segmentKey
      && (s.itemKey ?? null) === itemKey,
  );
}

/** The always-visible summary, which is also the accessible one.
 *
 * KEY-539: takes a vocabulary, defaulted to the platform's. The feed SURFACES deliberately do not pass one:
 * a feed describes two dials at once, each of which may call its action points something different, so
 * picking either dial's word would silently mislabel the other. The argument exists so the helper is ready
 * for whichever answer that product question gets, and the default keeps today's wording everywhere. */
export function feedTally(coverage: FeedPaintCoverage, noun: SegmentNoun = DEFAULT_SEGMENT_NOUN): string {
  if (coverage.fedSections === 0) return "Feeding nothing yet — click a wedge to choose";
  const sections = `${coverage.fedSections} of ${coverage.totalSections} ${coverage.totalSections === 1 ? "section" : "sections"}`;
  const segments = `${coverage.fedSegments} ${coverage.fedSegments === 1 ? noun.one : noun.many}`;
  // Items only earn a mention once some are held back — "31 of 44 items" is information, "44 of 44" is noise.
  const items =
    coverage.totalItems > 0 && coverage.fedItems < coverage.totalItems
      ? ` · ${coverage.fedItems} of ${coverage.totalItems} items`
      : "";
  return `Feeding ${sections} · ${segments}${items}${coverage.openEnded ? " · plus anything added later" : ""}`;
}
